import type { GameController } from './controller';

interface Caret {
  start: number;
  end: number;
}

/** Mount the report box once, outside the combat tree. Screen renders do not rebuild it. */
export function mountFeedbackDialog(root: HTMLElement, controller: GameController): () => void {
  let caret: Caret = { start: 0, end: 0 };
  let typing = false;

  const remember = (area: HTMLTextAreaElement): void => {
    controller.feedbackMessage = area.value;
    caret = {
      start: area.selectionStart ?? area.value.length,
      end: area.selectionEnd ?? area.value.length,
    };
  };

  const restore = (area: HTMLTextAreaElement): void => {
    const start = Math.min(caret.start, area.value.length);
    const end = Math.min(caret.end, area.value.length);
    area.focus();
    area.setSelectionRange(start, end);
  };

  const updateChrome = (): void => {
    const bug = root.querySelector<HTMLInputElement>('input[value="bug"]');
    const idea = root.querySelector<HTMLInputElement>('input[value="idea"]');
    if (bug) bug.checked = controller.feedbackKind === 'bug';
    if (idea) idea.checked = controller.feedbackKind === 'idea';
    const shot = root.querySelector<HTMLInputElement>('[data-action="feedback-shot"]');
    if (shot) shot.checked = controller.feedbackShot;
    const send = root.querySelector<HTMLButtonElement>('[data-action="feedback-send"]');
    if (send) {
      send.disabled = controller.feedbackSending;
      send.textContent = controller.feedbackSending ? 'Sending' : 'Send';
    }
    const note = root.querySelector<HTMLElement>('.modal-note');
    if (controller.feedbackNotice) {
      const line = note ?? document.createElement('p');
      line.className = 'modal-note';
      line.textContent = controller.feedbackNotice;
      if (!note) send?.parentElement?.before(line);
    } else {
      note?.remove();
    }
  };

  const paint = (): void => {
    if (!controller.feedbackOpen) {
      typing = false;
      root.replaceChildren();
      return;
    }
    let area = root.querySelector<HTMLTextAreaElement>('[data-action="feedback-message"]');
    if (!area) {
      root.replaceChildren(buildForm(controller));
      area = root.querySelector<HTMLTextAreaElement>('[data-action="feedback-message"]');
      if (!area) return;
      area.value = controller.feedbackMessage;
      typing = true;
      restore(area);
    } else if (area.value !== controller.feedbackMessage) {
      area.value = controller.feedbackMessage;
      if (typing) restore(area);
    } else if (typing && document.activeElement !== area) {
      restore(area);
    }
    updateChrome();
  };

  root.addEventListener('input', (event) => {
    const area = event.target as HTMLTextAreaElement;
    if (area.dataset?.action === 'feedback-message') remember(area);
  });
  root.addEventListener('keyup', (event) => {
    const area = event.target as HTMLTextAreaElement;
    if (area.dataset?.action === 'feedback-message') remember(area);
  });
  root.addEventListener('mouseup', (event) => {
    const area = event.target as HTMLTextAreaElement;
    if (area.dataset?.action === 'feedback-message') remember(area);
  });
  root.addEventListener('focusin', (event) => {
    const area = event.target as HTMLTextAreaElement;
    if (area.dataset?.action === 'feedback-message') typing = true;
  });
  root.addEventListener('change', (event) => {
    const input = event.target as HTMLInputElement;
    if (input.dataset.action === 'feedback-kind' && (input.value === 'bug' || input.value === 'idea')) {
      controller.feedbackKind = input.value;
    }
    if (input.dataset.action === 'feedback-shot') controller.feedbackShot = input.checked;
  });
  root.addEventListener('submit', (event) => {
    const form = (event.target as HTMLElement).closest('form');
    if (!form?.querySelector('[data-action="feedback-send"]')) return;
    event.preventDefault();
    void controller.submitFeedback();
  });
  root.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (button?.dataset.action === 'feedback-close') controller.closeFeedback();
  });

  const unsub = controller.subscribe(() => paint());
  paint();
  return () => {
    unsub();
    root.replaceChildren();
  };
}

function buildForm(controller: GameController): HTMLElement {
  const cloud = controller.account.mode === 'cloud';
  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <div class="modal-backdrop">
      <form class="modal-card" role="dialog" aria-labelledby="feedback-title" data-action="feedback-form">
        <p class="eyebrow">Last Spindle</p>
        <h2 id="feedback-title">Report a bug / idea</h2>
        <div class="button-row">
          <label><input type="radio" name="feedback-kind" data-action="feedback-kind" value="bug"/> Bug</label>
          <label><input type="radio" name="feedback-kind" data-action="feedback-kind" value="idea"/> Idea</label>
        </div>
        <textarea data-action="feedback-message" maxlength="2000" placeholder="What happened, or what should change?"></textarea>
        ${
          cloud
            ? '<label class="fine"><input type="checkbox" data-action="feedback-shot"/> Include a screenshot of the field</label>'
            : '<p class="fine">This save stays on this browser. The note is kept here and sends after a cloud save is signed in.</p>'
        }
        <div class="button-row">
          <button type="submit" data-action="feedback-send">Send</button>
          <button type="button" data-action="feedback-close">Close</button>
        </div>
      </form>
    </div>`;
  return wrap.firstElementChild as HTMLElement;
}
